import { AddTokenToWalletButton } from '@/components/add-token-to-wallet-button';
import { RoundedBadge } from '@/components/rounded-badge';
import { RoundedButton } from '@/components/rounded-button';
import { useAuth } from '@/hooks/useAuth';
import { useNucleusRoycoPositionsData, useRoycoPositionDataForVaultKey } from '@/hooks/useRoycoPositionsData';
import { useVaultPortfolioData } from '@/hooks/useVaultPortfolioData';
import { getTimeUntilUnlock } from '@/utils';
import { Card, FormatNumber, Heading, HStack, SimpleGrid, Skeleton, Text, VStack } from '@chakra-ui/react';
import { useAppKit } from '@reown/appkit/react';
import { useMemo } from 'react';
import { PiHourglassSimpleHighFill } from 'react-icons/pi';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useCurrentVault } from './DepositsAndWithdrawals/hooks/useCurrentVault';
import { DepositCtaToastButton, Position } from './DepositsAndWithdrawals/VaultBalance';

export const MobileVaultBalance = () => {
  const { pathname } = useLocation();
  const { vaultAddress } = useParams() as { vaultAddress: EthAddress };
  const { isAuthenticated } = useAuth();
  const { open } = useAppKit();
  const navigate = useNavigate();
  const { vault, isLoadingVault, vaultKey, marketId } = useCurrentVault();
  const { decimals } = vault?.token ?? {};
  const {
    isLoadingRoycoPositionsData,
    totalPlumeClaimable,
    totalPlumeEarned,
    roycoPositionsData,
    countsTowardsBalance,
  } = useNucleusRoycoPositionsData();

  const { wPlumeEarningsUsd, currentVaultRoycoPositionData } = useRoycoPositionDataForVaultKey({ vaultKey }) ?? {};

  const oldestPosition = currentVaultRoycoPositionData?.at(0);

  const { vaultPortfolioData, isLoadingVaultPortfolioData } = useVaultPortfolioData();
  const currentVaultBalanceData = vaultPortfolioData?.vaults?.[vaultAddress.toLowerCase()];
  const vaultBalanceUSD = currentVaultBalanceData?.total_value_in_usd ?? 0;
  const vaultBalanceNative = currentVaultBalanceData?.total_value_in_base ?? 0;

  const totalBalanceUsd = useMemo(() => {
    const totalRoycoBalanceUsd =
      roycoPositionsData
        ?.filter((position) => position.marketId === marketId && countsTowardsBalance(position))
        .reduce((total, position) => total + position.balanceInUsd, 0) ?? 0;
    return totalRoycoBalanceUsd + vaultBalanceUSD + wPlumeEarningsUsd;
  }, [countsTowardsBalance, marketId, roycoPositionsData, vaultBalanceUSD, wPlumeEarningsUsd]);

  const totalRoycoBalanceNative =
    roycoPositionsData
      ?.filter((position) => position.marketId === marketId && countsTowardsBalance(position))
      .reduce((total, position) => total + position.balanceScaledDown, 0) ?? 0;

  return (
    <Card.Root rounded="16px" bg="bg" py={6} px={4} w="full">
      <Card.Body p={0}>
        <VStack gap={6} w="full">
          <VStack gap={4} w="full" alignItems="flex-start">
            <HStack justifyContent="space-between" w="full" alignItems="center">
              <Heading fontSize="xl" fontWeight="700" lineHeight="28px" letterSpacing="-0.47px">
                Total Balance
              </Heading>
              {isAuthenticated && (
                <RoundedButton
                  onClick={() => {
                    navigate(`${pathname}/deposit`);
                  }}
                >
                  Deposit
                </RoundedButton>
              )}
              {!isAuthenticated && <RoundedButton onClick={() => open()}>Connect</RoundedButton>}
            </HStack>
            <Text fontSize="30px">
              <Skeleton loading={isLoadingVaultPortfolioData || isLoadingRoycoPositionsData}>
                <FormatNumber
                  value={totalBalanceUsd}
                  maximumFractionDigits={2}
                  minimumFractionDigits={2}
                  style="currency"
                  currency="USD"
                />
              </Skeleton>
            </Text>
          </VStack>
          <VStack gap={4} w="full" alignItems="flex-start">
            {totalBalanceUsd > 0 && (
              <Position
                w="full"
                title="Vault Position"
                value={vaultBalanceNative}
                tokenSymbol={vaultKey}
                tokenAddress={vaultAddress}
                tokenDecimals={decimals}
                loading={isLoadingVault || isLoadingVaultPortfolioData}
                onClickWithdraw={() => navigate(`${pathname}/withdraw`)}
              />
            )}
            {totalRoycoBalanceNative ? (
              <HStack>
                <Text fontSize="sm" color="fg.muted" fontWeight="500">
                  <FormatNumber value={totalRoycoBalanceNative} /> held in Royco
                </Text>
                <RoundedBadge fontSize="sm" color="fg.muted" fontWeight="500">
                  <PiHourglassSimpleHighFill />{' '}
                  {getTimeUntilUnlock(Number(oldestPosition?.unclaimedWPlumeIncentiveToken?.unlockTimestamp ?? 0))}
                </RoundedBadge>
              </HStack>
            ) : null}
            <HStack justifyContent="start">
              <AddTokenToWalletButton
                tokenSymbol={vaultKey}
                tokenAddress={vaultAddress}
                tokenDecimals={decimals}
                p={0.5}
                fontSize="sm"
                h="fit-content"
                color="fg.muted"
                loading={isLoadingVault}
              />
            </HStack>
            {marketId && vaultBalanceNative > 0 && (
              <DepositCtaToastButton
                onClick={() => navigate(`${pathname}/deposit?royco_only=true`)}
                availableBalance={vaultBalanceNative}
                w="full"
                h="auto"
                backgroundImage="none"
                rounded="16px"
                bg="#236B53"
                p={1}
                backgroundSize="cover"
                position="relative"
                overflow="hidden"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="165"
                  height="165"
                  viewBox="0 0 165 165"
                  fill="none"
                  style={{
                    position: 'absolute',
                    bottom: '0',
                    right: '0',
                    scale: 10,
                  }}
                >
                  <path
                    d="M87.3837 0.939307C83.6441 -0.313102 81.3559 -0.313102 77.6163 0.939307C68.4937 3.99457 68.8894 23.3295 71.7084 42.4059C64.5711 24.4918 55.2082 7.57053 45.7845 9.50716C41.9215 10.301 39.9424 11.4496 37.3367 14.4099C31.0704 21.5291 40.746 37.6888 52.5314 52.5971C37.5972 40.8445 21.4163 31.2043 14.3109 37.4863C11.3563 40.0985 10.2121 42.0801 9.42671 45.9448C7.52383 55.3086 24.2229 64.5716 42.0164 71.6512C23.0671 68.8845 3.97378 68.5558 0.939307 77.6163C-0.313102 81.3559 -0.313102 83.6441 0.939307 87.3837C3.97378 96.4442 23.0671 96.1155 42.0164 93.3488C24.2229 100.428 7.52376 109.691 9.42664 119.055C10.212 122.92 11.3563 124.902 14.3108 127.514C21.4161 133.796 37.5968 124.156 52.5309 112.403C40.7456 127.311 31.0702 143.471 37.3365 150.59C39.9422 153.55 41.9213 154.699 45.7842 155.493C55.2081 157.43 64.5712 140.508 71.7086 122.593C68.8894 141.67 68.4936 161.005 77.6163 164.061C81.3559 165.313 83.6441 165.313 87.3837 164.061C96.5066 161.005 96.1106 141.669 93.2913 122.592C100.429 140.507 109.792 157.43 119.216 155.493C123.079 154.699 125.058 153.55 127.664 150.59C133.93 143.471 124.255 127.311 112.469 112.403C127.403 124.156 143.584 133.796 150.689 127.514C153.644 124.902 154.788 122.92 155.574 119.055C157.477 109.691 140.777 100.428 122.984 93.3489C141.933 96.1155 161.026 96.4441 164.061 87.3837C165.313 83.6441 165.313 81.3559 164.061 77.6163C161.026 68.5559 141.933 68.8845 122.984 71.6512C140.777 64.5715 157.476 55.3086 155.574 45.9448C154.788 42.0801 153.644 40.0985 150.689 37.4863C143.584 31.2043 127.403 40.8445 112.469 52.5971C124.254 37.6888 133.93 21.5291 127.664 14.4099C125.058 11.4496 123.079 10.301 119.216 9.50716C109.792 7.57049 100.429 24.4925 93.2914 42.407C96.1106 23.3303 96.5065 3.99463 87.3837 0.939307Z"
                    fill="#5FD4AD"
                  />
                </svg>
              </DepositCtaToastButton>
            )}
            <SimpleGrid gap={4} columns={1} mt={2} w="full">
              <Card.Root borderRadius="2xl" w="full">
                <Card.Body p={4}>
                  <Text fontSize="sm" color="fg.muted" fontWeight="500">
                    Claimable $PLUME Incentives
                  </Text>
                  <Text fontSize="md" fontWeight="500" color="#00827B">
                    <FormatNumber value={totalPlumeClaimable} maximumFractionDigits={2} /> PLUME
                  </Text>
                </Card.Body>
              </Card.Root>
              <Card.Root borderRadius="2xl" w="full">
                <Card.Body p={4}>
                  <Text fontSize="sm" color="fg.muted" fontWeight="500">
                    Pending $PLUME Incentives
                  </Text>
                  <Text fontSize="md" fontWeight="500" color="#00827B">
                    <FormatNumber value={totalPlumeEarned} maximumFractionDigits={2} /> PLUME
                  </Text>
                </Card.Body>
              </Card.Root>
            </SimpleGrid>
          </VStack>
        </VStack>
      </Card.Body>
    </Card.Root>
  );
};
